import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import {
  STATUS_PRODUTO_LABEL,
  type StatusProduto,
} from '@/services/SimplesNacional/Segregacao/api/types';
import type { SegregacaoCalcularResponseData } from '@/services/SimplesNacional/Segregacao/api/types';

// Tailwind color → hex mapping for Recharts
const STATUS_HEX: Record<StatusProduto, string> = {
  X: '#6b7280', // gray-500
  XM: '#9ca3af', // gray-400
  A: '#eab308', // yellow-500
  '9': '#ef4444', // red-500
  '2': '#f97316', // orange-500
  '3': '#3b82f6', // blue-500
  '4': '#6366f1', // indigo-500
  '5': '#a855f7', // purple-500
  '7': '#06b6d4', // cyan-500
  '8': '#14b8a6', // teal-500
};

interface Props {
  segregacao: SegregacaoCalcularResponseData;
  statusSelecionado: string | null;
  onClickStatus: (status: string | null) => void;
}

function contarPorStatus(segregacao: SegregacaoCalcularResponseData) {
  const todos = [
    ...(segregacao.IGN ?? []),
    ...(segregacao.REV ?? []),
    ...(segregacao.SEG ?? []),
    ...(segregacao.NGR ?? []),
  ];

  const contagem: Partial<Record<StatusProduto, number>> = {};
  for (const p of todos) {
    const s = p.Status as StatusProduto;
    if (s) contagem[s] = (contagem[s] ?? 0) + 1;
  }
  return contagem;
}

export function StatusProdutosChart({ segregacao, statusSelecionado, onClickStatus }: Props) {
  const contagem = contarPorStatus(segregacao);

  const dados = (Object.keys(contagem) as StatusProduto[])
    .filter(s => (contagem[s] ?? 0) > 0)
    .map(s => ({
      status: s,
      label: STATUS_PRODUTO_LABEL[s]?.label ?? s,
      value: contagem[s] ?? 0,
      fill: STATUS_HEX[s] ?? '#6b7280',
    }));

  if (dados.length === 0) return null;

  const total = dados.reduce((acc, d) => acc + d.value, 0);

  return (
    <div className="flex flex-col gap-4 md:flex-row md:items-center">
      {/* Donut */}
      <div className="w-48 h-48 shrink-0 mx-auto md:mx-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={dados}
              dataKey="value"
              innerRadius={55}
              outerRadius={80}
              paddingAngle={2}
              onClick={entry => {
                const s = entry.status as string;
                onClickStatus(statusSelecionado === s ? null : s);
              }}
              style={{ cursor: 'pointer' }}
            >
              {dados.map(d => (
                <Cell
                  key={d.status}
                  fill={d.fill}
                  opacity={statusSelecionado && statusSelecionado !== d.status ? 0.35 : 1}
                  stroke={statusSelecionado === d.status ? '#fff' : 'transparent'}
                  strokeWidth={2}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number, _name, entry) => [
                `${value} produto(s)`,
                entry.payload?.label,
              ]}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Legenda + total */}
      <div className="flex-1 space-y-1">
        <p className="text-xs text-muted-foreground mb-2">
          Total: <strong>{total}</strong> produto(s)
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
          {dados.map(d => (
            <button
              key={d.status}
              className={`flex items-center gap-2 text-xs text-left rounded px-1 py-0.5 transition-opacity ${
                statusSelecionado && statusSelecionado !== d.status ? 'opacity-40' : ''
              }`}
              onClick={() => onClickStatus(statusSelecionado === d.status ? null : d.status)}
            >
              <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: d.fill }} />
              <span className="text-muted-foreground">{d.label}</span>
              <span className="ml-auto font-medium tabular-nums">{d.value}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
